import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { AdStatus } from '@/types';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { ROUTES } from '@/constants/routes';
import { useNavigationStore } from '@/store/useNavigationStore';

const isBrowser = typeof window !== 'undefined';

export const useNavigation = () => {
  const router = useRouter();
  const { adDetails } = useAdDetails();
  const customPush = useCustomPush();
  const [
    referrer,
    canGoBack,
    navigationType,
    setReferrer,
    setCanGoBack,
    setNavigationType,
    setGoBack,
  ] = useNavigationStore(state => [
    state.referrer,
    state.canGoBack,
    state.navigationType,
    state.setReferrer,
    state.setCanGoBack,
    state.setNavigationType,
    state.setGoBack,
  ]);

  const { status: adStatus } = adDetails ?? {};
  const currentUrl = isBrowser ? window.location.href : '';
  const storageInitialUrl = isBrowser
    ? sessionStorage.getItem('initialUrl')
    : null;

  const isPageReloaded = navigationType === 'reload';
  const isPopState = navigationType === 'back_forward';
  const isSameDomain = isBrowser && referrer.includes(window.location.hostname);
  const isAwaitingPaymentAdPage = adStatus === AdStatus.AwaitingPayment;
  const hasInitialUrl = !!storageInitialUrl;
  const isAtInitialPage = storageInitialUrl === currentUrl;
  const canGoBackValue =
    hasInitialUrl &&
    (isPageReloaded || isPopState ? !isAtInitialPage : isSameDomain);

  useEffect(() => {
    if (canGoBack === undefined) {
      setCanGoBack(canGoBackValue);
    }

    setGoBack(goBack);
  }, []);

  useEffect(() => {
    setCanGoBack(canGoBackValue);

    const handleRouteChange = () => {
      setReferrer(currentUrl);
      setNavigationType('navigate');
      handleChangeInitialUrl();
    };

    const handleChangeInitialUrl = () => {
      if (!storageInitialUrl) {
        sessionStorage.setItem('initialUrl', currentUrl);
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);
    window.addEventListener('pagehide', handleChangeInitialUrl);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      window.removeEventListener('pagehide', handleChangeInitialUrl);
    };
  }, [router.asPath]);

  function goBack() {
    if (!isBrowser) return;

    if (isAwaitingPaymentAdPage) {
      customPush(ROUTES.profileAds(AdStatus.Pending));
      return;
    }

    window.history.back();
  }
};
